import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { authedFetchJson, authedApiJsonOrThrow } from '../../utils/api';
import { navigate } from '../../router/router';
import { getClientSessionId } from '../../utils/clientSession';

type SurpriseOffer = {
  id: number;
  title: string;
  subtitle: string;
  image_url: string;
  cta_text: string;
  cta_url: string;
  internal_route: string | null;
  outbound_url?: string | null;
};

export function useSurpriseOffer(commandsCount: number, messagesCount: number) {
  const [offer, setOffer] = useState<SurpriseOffer | null>(null);
  const [visible, setVisible] = useState(false);
  const [impressionId, setImpressionId] = useState<number | null>(null);
  const boot = bootOnce();
  const lastCheckedKey = useRef('');
  const checking = useRef(false);

  // Check if we should show an offer whenever either counter changes
  const totalCount = commandsCount + messagesCount;

  useEffect(() => {
    const key = `${commandsCount}:${messagesCount}`;
    if (key === lastCheckedKey.current) return;
    if (totalCount < 1) return; // Need at least one interaction
    if (checking.current) return;

    lastCheckedKey.current = key;
    checking.current = true;

    (async () => {
      try {
        const sessionId = getClientSessionId();
        const r = await authedFetchJson<{
          show: boolean;
          offer: SurpriseOffer | null;
        }>(boot, `/api/surprise-offers/check?commands_count=${commandsCount}&messages_count=${messagesCount}&session_id=${encodeURIComponent(sessionId)}`);

        if (r.ok && r.data && r.data.show && r.data.offer) {
          setOffer(r.data.offer);
          setVisible(true);

          // Record impression
          try {
            const impRes = await authedApiJsonOrThrow<{ impression_id: number | null }>(
              boot,
              `/api/surprise-offers/${r.data.offer.id}/impression`,
              {
                method: 'POST',
                body: JSON.stringify({ session_id: sessionId }),
              }
            );
            setImpressionId(impRes.impression_id || null);
          } catch (e: any) {
            console.warn('[SurpriseOffer] impression error', e?.message || e);
          }
        }
      } catch (e: any) {
        console.warn('[SurpriseOffer] check error', e?.message || e);
      }
      checking.current = false;
    })();
  }, [boot, commandsCount, messagesCount, totalCount]);

  const close = useCallback(() => {
    setVisible(false);
    setOffer(null);
    setImpressionId(null);
  }, []);

  const handleCtaClick = useCallback(async () => {
    if (!offer) return;

    // Record click
    try {
      await authedApiJsonOrThrow(boot, `/api/surprise-offers/${offer.id}/click`, {
        method: 'POST',
        body: JSON.stringify({ impression_id: impressionId }),
      });
    } catch (e: any) {
      console.warn('[SurpriseOffer] click track error', e?.message || e);
    }

    // Handle navigation
    if (offer.internal_route) {
      // Internal routes like 'topup', 'premium', 'tasks'
      if (offer.internal_route === 'topup') {
        try {
          window.dispatchEvent(new CustomEvent('aicams-open-topup-sheet', { detail: { origin: 'surprise_offer' } }));
        } catch {}
      } else if (offer.internal_route === 'premium') {
        try {
          window.dispatchEvent(new CustomEvent('aicams-open-premium-sheet', { detail: { context: { origin: 'surprise_offer' } } }));
        } catch {}
      } else if (offer.internal_route === 'tasks') {
        navigate({ name: 'tasks', from: 'storefront' });
      }
    } else if (offer.cta_url) {
      // External URL
      const url = String((offer.outbound_url || offer.cta_url) || '').trim();
      if (!url) {
        close();
        return;
      }
      try {
        const tg = (window as any).Telegram?.WebApp;
        if (tg?.openLink) {
          tg.openLink(url);
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }

    close();
  }, [boot, close, impressionId, offer]);

  return { offer, visible, close, handleCtaClick };
}

export function SurpriseOfferOverlay(props: {
  offer: SurpriseOffer;
  onClose: () => void;
  onCtaClick: () => void;
}) {
  const { offer, onClose, onCtaClick } = props;
  const [closing, setClosing] = useState(false);
  const boot = bootOnce();
  const { t } = useI18n(boot.i18n);

  const reducedMotion = useMemo(() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    } catch {
      return false;
    }
  }, []);

  const handleClose = useCallback(() => {
    if (reducedMotion) {
      onClose();
      return;
    }
    setClosing(true);
    setTimeout(onClose, 200);
  }, [onClose, reducedMotion]);

  return (
    <div
      class={`surprise-overlay ${closing ? 'closing' : ''}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(30,0,10,0.98) 100%)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
        animation: closing
          ? 'surpriseFadeOut 200ms ease-out forwards'
          : 'surpriseFadeIn 200ms ease-out',
      }}
      onClick={handleClose}
    >
      <div
        class={`surprise-content ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '320px',
          animation: closing
            ? 'surpriseScaleOut 200ms ease-out forwards'
            : 'surpriseScaleIn 300ms ease-out',
        }}
      >
        {/* Badge + Close row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #DC143C, #FF6B6B)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              textTransform: 'uppercase',
              padding: '8px 16px',
              borderRadius: '20px',
              letterSpacing: '0.5px',
            }}
          >
            {t('surprise.badge', 'Special Offer')}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1.5px solid rgba(255,255,255,0.25)',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              color: 'rgba(255,255,255,0.9)',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Image */}
        {offer.image_url && (
          <div
            style={{
              width: '240px',
              height: '200px',
              marginBottom: '20px',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 0 40px rgba(220,20,60,0.3)',
              background: 'linear-gradient(135deg, #2a0a1a, #1a0510)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={offer.image_url}
              alt=""
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
        )}

        {/* Title */}
        <h2
          style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#fff',
            margin: '0 0 12px 0',
            lineHeight: 1.2,
          }}
        >
          {offer.title || t('surprise.title', 'Special Offer!')}
        </h2>

        {/* Subtitle */}
        {offer.subtitle && (
          <p
            style={{
              fontSize: '16px',
              color: 'rgba(255,255,255,0.7)',
              margin: '0 0 24px 0',
              lineHeight: 1.5,
            }}
          >
            {offer.subtitle}
          </p>
        )}

        {/* CTA Button */}
        <button
          onClick={onCtaClick}
          style={{
            width: '100%',
            maxWidth: '260px',
            height: '56px',
            borderRadius: '28px',
            border: 'none',
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            color: '#000',
            fontSize: '18px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(255,215,0,0.3)',
            transition: 'transform 100ms',
          }}
        >
          {offer.cta_text || t('surprise.cta', 'Get Now')}
        </button>

        {/* Premium upsell — clickable, opens PremiumSheet */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            try {
              window.dispatchEvent(new CustomEvent('aicams-open-premium-sheet', { detail: { context: { origin: 'surprise_offer_upsell' } } }));
            } catch {}
            onClose();
          }}
          style={{
            marginTop: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '16px' }}>💎</span>
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.3 }}>
              {t('surprise.premium_upsell', 'Remove all ads with')}
              {' '}<strong style={{ color: '#FFD700', fontWeight: 700 }}>Premium</strong>
            </span>
          </div>
          <span
            style={{
              fontSize: '12px',
              color: 'rgba(255,215,0,0.7)',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {t('surprise.premium_cta', 'Get Premium →')}
          </span>
        </div>

      </div>

      <style>{`
        @keyframes surpriseFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes surpriseFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes surpriseScaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes surpriseScaleOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.8);
          }
        }

        .surprise-content button:hover {
          transform: scale(1.02);
        }

        .surprise-content button:active {
          transform: scale(0.98);
        }
      `}</style>
    </div>
  );
}
